import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { LoginContext } from '../../MainLayout/MainLayout';
import { setRedirectPath } from '../../auth/utils/storage';

// ─── Inline Icons ──────────────────────────────────────────────────────────────
const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const BadgeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);
const WalletIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const HashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/>
    <line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>
  </svg>
);
const EmptyIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
    <path d="M7 8h10M7 12h6"/>
  </svg>
);
const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);

// ─── Tab config ────────────────────────────────────────────────────────────────
const TABS = [
  { label: 'Active',    value: 1, color: '#00BD7F', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  { label: 'Upcoming',  value: 2, color: '#3B82F6', bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500'    },
  { label: 'Expired',   value: 5, color: '#6B7280', bg: 'bg-gray-50',    text: 'text-gray-600',    border: 'border-gray-200',    dot: 'bg-gray-400'    },
];

// ─── Skeleton card ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm animate-pulse">
    <div className="h-24 bg-gray-100" />
    <div className="p-5 space-y-3">
      <div className="h-3 bg-gray-100 rounded-full w-3/4" />
      <div className="h-3 bg-gray-100 rounded-full w-1/2" />
      <div className="h-3 bg-gray-100 rounded-full w-2/3" />
      <div className="h-2 bg-gray-100 rounded-full w-full mt-4" />
    </div>
  </div>
);

// ─── Subscription Card ─────────────────────────────────────────────────────────
const SubscriptionCard = ({ sub, tabColor }) => {
  const isActive   = sub.status_label?.toLowerCase() === 'active';
  const isExpired  = sub.status_label?.toLowerCase() === 'expired';
  const isUpcoming = sub.status_label?.toLowerCase() === 'upcoming';

  const statusStyle = isActive
    ? { bg: 'bg-emerald-100', text: 'text-emerald-700' }
    : isExpired
    ? { bg: 'bg-gray-100',    text: 'text-gray-500'   }
    : { bg: 'bg-blue-100',    text: 'text-blue-700'   };

  const daysTotal   = sub.start_date && sub.end_date
    ? moment(sub.end_date).diff(moment(sub.start_date), 'days')
    : null;
  const daysUsed    = sub.start_date
    ? moment().diff(moment(sub.start_date), 'days')
    : null;
  const progressPct = daysTotal && daysUsed !== null
    ? Math.min(100, Math.max(0, Math.round((daysUsed / daysTotal) * 100)))
    : null;

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
      {/* Card header gradient */}
      <div
        className="relative px-5 pt-5 pb-8"
        style={{ background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)' }}
      >
        {/* Status badge */}
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : isExpired ? 'bg-gray-400' : 'bg-blue-500'}`} />
          {sub.status_label || '—'}
        </span>

        {/* Plan name */}
        <h3
          className="mt-3 text-base font-bold leading-snug text-[#004276]"
          style={{ color: 'var(--color-brand-primary)' }}
        >
          {sub.plan_name || '—'}
        </h3>

        {/* Amount pill */}
        {sub.amount && (
          <span className="absolute top-5 right-5 bg-white/80 backdrop-blur-sm text-[#004276] text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
            ₹{sub.amount}
          </span>
        )}
      </div>

      {/* Card body */}
      <div className="px-5 pt-4 pb-5 -mt-4 bg-white rounded-t-2xl space-y-3">

        {/* Subscription ID */}
        {sub.subscription_id && (
          <div className="flex items-center gap-2 text-gray-500">
            <HashIcon />
            <span className="text-xs truncate">{sub.subscription_id}</span>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Start</p>
            <div className="flex items-center gap-1.5 text-gray-700">
              <CalendarIcon />
              <span className="text-xs font-medium">{sub.start_date ? moment(sub.start_date).format('DD MMM YY') : '—'}</span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Expiry</p>
            <div className="flex items-center gap-1.5 text-gray-700">
              <CalendarIcon />
              <span className="text-xs font-medium">{sub.end_date ? moment(sub.end_date).format('DD MMM YY') : '—'}</span>
            </div>
          </div>
        </div>

        {/* Days remaining progress (active only) */}
        {isActive && sub.days_remaining !== null && sub.days_remaining !== undefined && progressPct !== null && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5 text-gray-500">
                <ClockIcon />
                <span className="text-xs font-medium text-gray-600">{sub.days_remaining} days remaining</span>
              </div>
              <span className="text-xs text-gray-400">{progressPct}%</span>
            </div>
            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  background: progressPct > 75
                    ? 'linear-gradient(90deg, #00BD7F, #00B5E2)'
                    : progressPct > 40
                    ? 'linear-gradient(90deg, #F59E0B, #F97316)'
                    : 'linear-gradient(90deg, #EF4444, #F97316)',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function MySubscription() {
  const navigate    = useNavigate();
  const { openLogin } = useContext(LoginContext);

  const [activeTab,  setActiveTab]  = useState(0);
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const userData = (() => {
    try { return JSON.parse(localStorage.getItem('loginObj') || 'null'); } catch { return null; }
  })();

  // Redirect to login if not logged in — come back here after login
  useEffect(() => {
    if (!userData?.code) {
      setRedirectPath('/my-subscription');
      openLogin();
      navigate('/caresathi');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptions = (tabIdx) => {
    if (!userData?.code) return;
    const statusValue = TABS[tabIdx].value;
    setLoading(true);
    setError('');

    fetch(
      `${process.env.REACT_APP_CARE_SATHI}ds/subscription/patient/${userData.code}/status?status=${statusValue}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && data?.data?.subscriptions) {
          setList(data.data.subscriptions);
        } else {
          setList([]);
        }
      })
      .catch(() => {
        setError('Failed to load subscriptions. Please try again.');
        setList([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSubscriptions(activeTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const currentTab = TABS[activeTab];

  return (
    <div
      className="min-h-screen pt-0"
      style={{ background: 'var(--section-bg-gradient), #fff' }}
    >
      {/* ── Page hero ── */}
      <div
        className="px-4 md:px-10 py-10 md:py-14 relative overflow-hidden"
        style={{ background: 'var(--color-brand-primary)' }}
      >
        {/* Soft blobs */}
        <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none" />

        <div className="max-w-5xl mx-auto relative">
          <p className="text-white/70 text-sm font-medium mb-1">CareSathi</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">My Subscriptions</h1>
          <p className="text-white/60 text-sm">View and manage all your CareSathi subscription plans</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 pb-16">
        {/* ── Tabs ── */}
        <div className="flex gap-2 mt-6 mb-8 overflow-x-auto pb-1">
          {TABS.map((tab, i) => (
            <button
              key={tab.label}
              onClick={() => { setActiveTab(i); setList([]); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 border ${
                activeTab === i
                  ? 'text-white shadow-md border-transparent scale-[1.02]'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
              }`}
              style={activeTab === i ? { background: 'var(--color-brand-primary)', borderColor: 'transparent' } : {}}
            >
              <span className={`w-2 h-2 rounded-full ${activeTab === i ? 'bg-white' : tab.dot}`} />
              {tab.label}
            </button>
          ))}

          {/* Refresh button */}
          <button
            onClick={() => fetchSubscriptions(activeTab)}
            disabled={loading}
            className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-gray-500 bg-white border border-gray-200 hover:border-gray-300 hover:text-gray-700 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <span className={loading ? 'animate-spin' : ''}><RefreshIcon /></span>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {error}
          </div>
        )}

        {/* ── Loading skeletons ── */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
          </div>
        )}

        {/* ── Subscription grid ── */}
        {!loading && list.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((sub, i) => (
              <SubscriptionCard key={sub.subscription_id || i} sub={sub} tabColor={currentTab.color} />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && list.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-24 h-24 rounded-full bg-gray-50 flex items-center justify-center mb-5">
              <EmptyIcon />
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-1">
              No {currentTab.label.toLowerCase()} subscriptions
            </h3>
            <p className="text-sm text-gray-400 max-w-xs mb-6">
              {activeTab === 0
                ? "You don't have any active CareSathi subscription right now."
                : activeTab === 1
                ? "No upcoming subscriptions scheduled."
                : "No expired subscriptions found."}
            </p>
            {activeTab !== 0 && (
              <button
                onClick={() => setActiveTab(0)}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-opacity"
                style={{ background: 'var(--color-brand-primary)' }}
              >
                View Active Plans
              </button>
            )}
            {activeTab === 0 && (
              <button
                onClick={() => navigate('/caresathi')}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-opacity"
                style={{ background: 'var(--color-brand-primary)' }}
              >
                Subscribe Now
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
